import { observer } from 'mobx-react';

import Icon from '../../../app/shared/components/Icon/Icon';
import AddProductsToPricelistModal from '../../shared/AddProductsToPricelistModal/AddProductsToPricelistModal';
import Brand from '../../shared/Brand/Brand';
import ExportPricelistToPdfModal from '../../shared/ExportPricelistToPdfModal/ExportPricelistToPdfModal';
import ProductAccessories from '../../shared/ProductAccessories/ProductAccessories';
import { ProductPricelist } from '../../shared/ProductPricelist/ProductPricelist';
import Button from '../../ui/Button/Button';
import DateTime from '../../ui/DateTime';
import Image from '../../ui/Image/Image';
import Markdown from '../../ui/Markdown/Markdown';
import { Backlink, TopBar, TopBarActions } from '../../ui/TopBar/TopBar';
import FamilyDetailsModal from './FamilyDetailsModal/FamilyDetailsModal';
import css from './ProductDetails.css';
import ProductDetailsState from './ProductDetailsState';
import TermsModal from './TermsModal/TermsModal';

interface ProductDetailsProps {
  state: ProductDetailsState;
}

export const ProductDetails = observer(({ state }: ProductDetailsProps) => {
  const { data, translation } = state;

  return (
    <main className={css.root}>
      <TopBar className={css.topBar}>
        <Backlink to={state.backUrl}>{translation.backlinkLabel} </Backlink>
        <TopBarActions>
          <AddProductsToPricelistModal
            state={state.addToPricelistModal}
            trigger={
              <Button size="medium" theme="primary" variant="outline">
                <Icon className={css.addToPricelistIcon} name="filePlus" />
                {translation.addToPricelistModal.triggerLabel}
              </Button>
            }
          />
          <ExportPricelistToPdfModal
            state={state.exportToPdfModal}
            translation={translation.exportToPdfModal}
            trigger={
              <Button size="medium" theme="primary" variant="outline">
                <Icon className={css.downloadIcon} name="downloadLight" /> {translation.downloadLabel}
              </Button>
            }
          />
        </TopBarActions>
      </TopBar>
      <div className={css.mainContent}>
        <header>
          <div className={css.headerTop}>
            {/* TODO: Uncomment and render date if available in API response */}
            {/* <Date label={translation.lastUpdatedLabel} locale={state.locale} value={'2027-01-01'} /> */}
            <div className={css.moreDetailsButtons}>
              <FamilyDetailsModal data={data.familyDetails} translation={state.translation.familyDetails} />
              <TermsModal state={state.termsModal} />
            </div>
          </div>
          <Brand brandId={data.productDetails.brandId} brandName={data.productDetails.brandName} />
          <h1 className={css.heading}>{data.productDetails.name}</h1>
          <div className={css.metadata}>
            <span className={css.itemNoWrapper}>
              {translation.itemNoLabel}
              <span className={css.itemNo}>{data.productDetails.code}</span>
            </span>
            {data.productDetails.types.map((type) => (
              <span className={css.type} key={type}>
                {type}
              </span>
            ))}
          </div>
          {data.productDetails.keyFeatures && (
            <>
              <h2 className={css.detailsHeading}>{translation.productDetailsHeading}</h2>
              <Markdown className={css.details} content={data.productDetails.keyFeatures} />
            </>
          )}
        </header>
        <ProductPricelist
          displayOptions={data.productDetails.displayOptions}
          className={css.pricelist}
          basePrice={data.productDetails.basePrice}
          netBasePrice={data.productDetails.netBasePrice}
          priceGroups={data.productDetails.priceGroups}
          translation={translation.pricelist}
        />
        <ProductAccessories
          className={css.accessories}
          accessories={data.accessories}
          caption={translation.accessoriesHeading}
          translation={translation.accessories}
        />
        {data.productDetails.additionalInfo && (
          <section className={css.additionalInfo}>
            <h2 className={css.additionalInfoHeading}>{translation.additionalInfoHeading}</h2>
            <Markdown className={css.additionalInfoContent} content={data.productDetails.additionalInfo} />
          </section>
        )}
      </div>
      <div className={css.imageContainer}>
        <Image
          className={css.image}
          src={data.productDetails.image?.url ?? ''}
          alt={data.productDetails.image?.name ?? ''}
          width={360}
          height={360}
          scalingMode="fit"
          sharpeningFactor={2}
          size="contain"
        />
      </div>
    </main>
  );
});

interface DateProps {
  label: string;
  locale: string;
  value: string;
}

const Date = ({ label, locale, value }: DateProps) => {
  return (
    <span className={css.dateWrapper}>
      {label}
      <DateTime className={css.date} value={value} locale={locale} format="date-only" />
    </span>
  );
};
